import { useContext, useEffect, useState } from 'react';
import { CreditContext } from '../../context/CreditProvider';
import getSteps from '../../constants/steps';
import { useSettings } from '@sdf/base';
import { TourProvider } from '@reactour/tour';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useAppContext } from '../../hooks';
import { useMediaQuery, useTheme } from '@mui/system';
import { PUBLIC_PAGES } from '../../utils/constant';
import { API_CONSTANTS, axiosInstance } from '../../utils';
import {
  getTourCompletedVersion,
  getTourStorageKey,
  getTourUserEmail,
} from '../../utils/tourWelcome';

const navButtonBaseStyle = {
  borderRadius: '8px',
  border: '1px solid #524c58',
  background: '#1F1D27',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0%',
  padding: '10px 16px',
  cursor: 'pointer',
  transition: 'all 180ms ease',
};

const secondaryStyle = {
  ...navButtonBaseStyle,
  background: 'transparent',
  border: '1.5px solid #7B35EF',
  color: '#FFFFFF',
};

const primaryStyle = {
  ...navButtonBaseStyle,
  background: '#7B35EF',
  border: '1.5px solid #7B35EF',
  color: '#FFFFFF',
  boxShadow: '0 4px 12px rgba(123, 53, 239, 0.25)',
};

const enableAgentPlan = true;
const TOUR_DISABLED_ROUTES = ['/subscription', '/billing-details', '/profile'];
const TRIAL_TO_PAID_PLAN_MAP: Record<string, string> = {
  special_7_days_trial_all_access_monthly: 'all-access-plan-monthly',
  special_7_days_trial_all_access_yearly: 'all-access-plan-yearly',
  special_7_days_trial_agent_yearly: 'agent_plan_yearly',
  special_7_days_trial_agent_monthly: 'agent_plan_monthly',
};

const TourWrapper = ({ children, router }) => {
  const { userPlanData, agentPlanData, isFetchingCredit, hadPreviousActiveSubscription } =
    useContext(CreditContext);
  const { setIsDrawerOpen, userData } = useAppContext();
  const [tourStep, setTourStep] = useState(0);
  const [isTourSyncing, setIsTourSyncing] = useState(false);
  const [nextPlanData, setNextPlanData] = useState({
    isLoaded: false,
    amount: null as number | null,
    interval: '',
    credits: '',
    agentAddonAmount: null as number | null,
    agentAddonCredits: null as number | null,
  });
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isPublicPage = PUBLIC_PAGES.includes(router.pathname);
  const isTourDisabledRoute = TOUR_DISABLED_ROUTES.includes(router.pathname);
  const tourUserEmail = getTourUserEmail(userData);
  const tourStepKey = getTourStorageKey('tourStep', tourUserEmail);
  const tourInitializedKey = getTourStorageKey('tourInitialized', tourUserEmail);

  useEffect(() => {
    if (isPublicPage || isTourDisabledRoute || !userData) {
      return;
    }

    const getNextPlanData = async () => {
      setNextPlanData((prev) => ({ ...prev, isLoaded: false }));

      const subscriptions = Array.isArray(userData?.subscriptions) ? userData.subscriptions : [];
      const activeSubscriptions = subscriptions.filter((sub: any) => sub?.isActive);
      const activeMainPlanSub = activeSubscriptions.find((sub: any) => {
        const uniqueId = sub?.plan?.planUniqueId || sub?.planUniqueId;
        return uniqueId === 'all-access-plan-monthly' || uniqueId === 'all-access-plan-yearly';
      });

      const mainPlan = activeMainPlanSub?.plan;
      const activeTrialPlanSub = activeSubscriptions.find((sub: any) => {
        const uniqueId = String(sub?.plan?.planUniqueId || sub?.planUniqueId || '');
        return Boolean(TRIAL_TO_PAID_PLAN_MAP[uniqueId]);
      });
      const activeTrialUniqueId = String(
        activeTrialPlanSub?.plan?.planUniqueId || (activeTrialPlanSub as any)?.planUniqueId || ''
      );
      let activeMainUniqueId = String(
        mainPlan?.planUniqueId || (activeMainPlanSub as any)?.planUniqueId || ''
      );
      if (!activeMainUniqueId && activeTrialUniqueId) {
        activeMainUniqueId = TRIAL_TO_PAID_PLAN_MAP[activeTrialUniqueId] || activeMainUniqueId;
      }

      let mainAmount: number | null =
        typeof mainPlan?.amount === 'number' ? Number(mainPlan.amount) : null;
      let mainInterval = String(mainPlan?.interval || '');
      let mainCredits: number | null =
        typeof mainPlan?.credits === 'number' ? Number(mainPlan.credits) : null;

      let agentAddonAmount: number | null = null;
      let agentAddonCredits: number | null = null;

      try {
        const [activePlansResponse, activeSubsResponse] = await Promise.all([
          axiosInstance.get(API_CONSTANTS.ACTIVE_PLANS.path),
          axiosInstance.get(API_CONSTANTS.ACTIVE_SUBSCRIPTION.path),
        ]);

        const plans = Array.isArray(activePlansResponse?.data) ? activePlansResponse.data : [];
        const activeSubs = Array.isArray(activeSubsResponse?.data) ? activeSubsResponse.data : [];

        if (!activeMainUniqueId) {
          const activeMainFromApi = activeSubs.find((sub: any) => {
            const uniqueId = String(sub?.planUniqueId || sub?.plan?.planUniqueId || '');
            return uniqueId === 'all-access-plan-monthly' || uniqueId === 'all-access-plan-yearly';
          });
          const activeTrialFromApi = activeSubs.find((sub: any) => {
            const uniqueId = String(sub?.planUniqueId || sub?.plan?.planUniqueId || '');
            return Boolean(TRIAL_TO_PAID_PLAN_MAP[uniqueId]);
          });
          const resolvedMainFromApi = activeMainFromApi || activeTrialFromApi;

          if (resolvedMainFromApi) {
            const resolvedUniqueId = String(
              resolvedMainFromApi?.planUniqueId || resolvedMainFromApi?.plan?.planUniqueId || ''
            );
            const mappedUniqueId = TRIAL_TO_PAID_PLAN_MAP[resolvedUniqueId] || resolvedUniqueId;
            const isTrialMainFromApi = Boolean(TRIAL_TO_PAID_PLAN_MAP[resolvedUniqueId]);
            activeMainUniqueId = mappedUniqueId;

            if (isTrialMainFromApi && !mainInterval) {
              mainInterval = mappedUniqueId.includes('yearly') ? 'Yearly' : 'Monthly';
            }

            if (!isTrialMainFromApi) {
              if (mainAmount === null) {
                const amountFromSub =
                  typeof resolvedMainFromApi?.amount === 'number'
                    ? Number(resolvedMainFromApi.amount)
                    : typeof resolvedMainFromApi?.plan?.amount === 'number'
                    ? Number(resolvedMainFromApi.plan.amount)
                    : null;
                if (amountFromSub !== null) {
                  mainAmount = amountFromSub;
                }
              }

              if (!mainInterval) {
                const intervalFromSub = String(
                  resolvedMainFromApi?.interval || resolvedMainFromApi?.plan?.interval || ''
                );
                if (intervalFromSub) {
                  mainInterval = intervalFromSub;
                }
              }

              if (mainCredits === null) {
                const creditsFromSub =
                  typeof resolvedMainFromApi?.credits === 'number'
                    ? Number(resolvedMainFromApi.credits)
                    : typeof resolvedMainFromApi?.plan?.credits === 'number'
                    ? Number(resolvedMainFromApi.plan.credits)
                    : null;
                if (creditsFromSub !== null) {
                  mainCredits = creditsFromSub;
                }
              }
            }
          }
        }

        const backendMainPlan = plans.find(
          (plan: any) => plan?.planUniqueId === activeMainUniqueId
        );
        if (backendMainPlan) {
          if (mainAmount === null && typeof backendMainPlan.amount === 'number') {
            mainAmount = Number(backendMainPlan.amount);
          }
          if (!mainInterval && backendMainPlan.interval) {
            mainInterval = String(backendMainPlan.interval);
          }
          if (mainCredits === null && typeof backendMainPlan.credits === 'number') {
            mainCredits = Number(backendMainPlan.credits);
          }
        }

        if (mainAmount === null) {
          const fallbackMainPlanId =
            activeMainUniqueId ||
            (mainInterval === 'Yearly' ? 'all-access-plan-yearly' : 'all-access-plan-monthly');
          const fallbackMainPlan = plans.find(
            (plan: any) => plan?.planUniqueId === fallbackMainPlanId
          );

          if (fallbackMainPlan && typeof fallbackMainPlan.amount === 'number') {
            mainAmount = Number(fallbackMainPlan.amount);
          }
          if (!mainInterval && fallbackMainPlan?.interval) {
            mainInterval = String(fallbackMainPlan.interval);
          }
          if (mainCredits === null && typeof fallbackMainPlan?.credits === 'number') {
            mainCredits = Number(fallbackMainPlan.credits);
          }
        }

        const prefersYearly = mainInterval === 'Yearly';
        const preferredAgentPlanUniqueId = prefersYearly
          ? 'agent_plan_yearly'
          : 'agent_plan_monthly';

        const agentPlan =
          plans.find((plan: any) => plan?.planUniqueId === preferredAgentPlanUniqueId) ||
          plans.find(
            (plan: any) =>
              plan?.planUniqueId === 'agent_plan_monthly' ||
              plan?.planUniqueId === 'agent_plan_yearly'
          );

        if (agentPlan) {
          agentAddonAmount =
            typeof agentPlan.amount === 'number' ? Number(agentPlan.amount) : agentAddonAmount;
          agentAddonCredits =
            typeof agentPlan.credits === 'number' ? Number(agentPlan.credits) : agentAddonCredits;
        }
      } catch {
        // Keep UI stable; downstream step shows skeleton/placeholder until data is available.
      }

      if (!mainInterval) {
        mainInterval = activeMainUniqueId.includes('yearly') ? 'Yearly' : 'Monthly';
      }

      setNextPlanData({
        isLoaded: true,
        amount: mainAmount,
        interval: mainInterval,
        credits:
          typeof mainCredits === 'number'
            ? `${parseFloat(
                ((mainCredits / 1_000_000) * (mainInterval === 'Yearly' ? 12 : 1)).toFixed(2)
              )}M`
            : '',
        agentAddonAmount,
        agentAddonCredits,
      });

      return {
        mainAmount,
        mainInterval,
        agentAddonAmount,
      };
    };

    let isCancelled = false;

    const run = async () => {
      const maxAttempts = 8;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (isCancelled) return;

        const resolved = await getNextPlanData();
        const hasMainAmount = typeof resolved?.mainAmount === 'number';
        const hasAgentAddonAmount = typeof resolved?.agentAddonAmount === 'number';

        if (hasMainAmount && hasAgentAddonAmount) {
          return;
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    };

    run();

    return () => {
      isCancelled = true;
    };
  }, [userData, isPublicPage, isTourDisabledRoute]);

  useEffect(() => {
    const savedStep = localStorage.getItem(tourStepKey);
    if (savedStep) {
      setTourStep(Number(savedStep));
    }
  }, [tourStepKey]);

  const { onChangeWelcomeTour } = useSettings();

  const steps = getSteps(
    router,
    userPlanData || '',
    agentPlanData || '',
    enableAgentPlan,
    userData,
    nextPlanData,
    setIsTourSyncing,
    hadPreviousActiveSubscription
  );

  const disableBody = (target) => {
    const targetElement = target instanceof HTMLElement ? target : document.body;
    if (targetElement) {
      disableBodyScroll(targetElement);
    }
  };

  const enableBody = (target) => {
    const targetElement = target instanceof HTMLElement ? target : document.body;
    if (targetElement) {
      enableBodyScroll(targetElement);
    }
  };

  if (
    isPublicPage ||
    isTourDisabledRoute ||
    !userPlanData ||
    !agentPlanData ||
    isFetchingCredit ||
    !nextPlanData.isLoaded
  ) {
    return children;
  }

  return (
    <TourProvider
      key={`${userPlanData}-${agentPlanData}`}
      steps={steps}
      currentStep={tourStep}
      setCurrentStep={(step) => {
        const shouldOpenDrawer =
          Number(step) >= (userPlanData === 'activePlan' ? 1 : 2) && Number(step) <= 6;

        if (isExtraSmall) {
          setIsDrawerOpen?.(shouldOpenDrawer);

          // if (Number(step) === 7) {
          //   router.push('/text-to-image');
          // }

          // if (Number(step) <= 6) {
          //   router.push('/dashboard');
          // }

          setTimeout(() => {
            setTourStep(step);
            localStorage.setItem(tourStepKey, step.toString());
            localStorage.setItem(tourInitializedKey, 'true');
          }, 350);
          return;
        }

        // if (Number(step) === 7) {
        //   router.push('/text-to-image');
        //   setTimeout(() => {
        //     setTourStep(step);
        //     localStorage.setItem('tourStep', step.toString());
        //   }, 350);
        //   return;
        // }

        // if (Number(step) <= 6) {
        //   router.push('/dashboard');
        // }

        setTourStep(step);
        localStorage.setItem(tourStepKey, step.toString());
        localStorage.setItem(tourInitializedKey, 'true');
      }}
      disableInteraction
      onClickClose={({ setIsOpen, setCurrentStep }) => {
        setIsOpen(false);
        // setCurrentStep(0);
        // localStorage.removeItem('tourStep');
        // onChangeWelcomeTour('welcomeToT2CV2');
        window.dispatchEvent(new Event('tourSkipped'));
      }}
      onClickMask={(clickProps) => {
        clickProps.setIsOpen(false);
        localStorage.setItem(tourStepKey, clickProps.currentStep.toString());
        localStorage.setItem(tourInitializedKey, 'true');
        window.dispatchEvent(new Event('tourSkipped'));
      }}
      prevButton={({ currentStep, setCurrentStep }) => {
        if (currentStep <= 0) {
          return <span style={{ width: '78px' }} />;
        }

        return (
          <button
            type="button"
            style={navButtonBaseStyle}
            onClick={() => {
              setCurrentStep(currentStep - 1);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#322B44';
              e.currentTarget.style.borderColor = '#655A7A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = navButtonBaseStyle.background;
              e.currentTarget.style.borderColor = '#4A4457';
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <img
                src="/assets/icons/ArrowRight.svg"
                alt="ArrowRight"
                width="24px"
                height="24px"
                style={{ transform: 'rotate(180deg)', color: '#ffffff' }}
              />
              <span
                style={{
                  paddingTop: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: '100%',
                }}
              >
                Back
              </span>
            </div>
          </button>
        );
      }}
      nextButton={({ currentStep, stepsLength, setIsOpen, setCurrentStep }) => {
        const isFreeUser = userPlanData === 'freeTrialAccess' || userPlanData === 'noActivePlan';
        const trialUser = userPlanData === 'freeTrialAccess' || agentPlanData === 'agentTrialPlan';
        const noAgentUser =
          enableAgentPlan &&
          ((userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') ||
            agentPlanData === 'agentTrialPlan');

        if (currentStep === 0 && userPlanData === 'activePlan' && agentPlanData !== 'agentPlan') {
          return null;
        }

        if (currentStep <= 0) {
          if (isFreeUser || noAgentUser) {
            return (
              <button
                type="button"
                style={secondaryStyle}
                onClick={() => setCurrentStep(currentStep + 1)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(123, 53, 239, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {trialUser ? 'Continue Trial' : 'Take Tour'}
              </button>
            );
          }
        }

        const last = currentStep === stepsLength - 1;
        return (
          <button
            type="button"
            style={{
              ...navButtonBaseStyle,
              background: '#7B35EF',
              color: '#FFFFFF',
            }}
            onClick={() => {
              if (last) {
                setIsOpen(false);
                onChangeWelcomeTour(getTourCompletedVersion(tourUserEmail));
                localStorage.removeItem(tourStepKey);
                setTimeout(() => localStorage.removeItem(tourInitializedKey), 350);
              } else {
                setCurrentStep(currentStep + 1);
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.08)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {last ? (
              <span
                style={{
                  paddingTop: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: '100%',
                }}
              >
                Finish Tour
              </span>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span
                  style={{
                    paddingTop: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Inter, sans-serif',
                    lineHeight: '100%',
                  }}
                >
                  {currentStep === 1 && trialUser ? 'Take Tour' : 'Next'}
                </span>
                <img
                  src="/assets/icons/ArrowRight.svg"
                  alt="ArrowRight"
                  width="24px"
                  height="24px"
                  style={{ color: '#ffffff' }}
                />
              </div>
            )}
          </button>
        );
      }}
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': '#7B35EF',
          backgroundColor: '#1F1D27',
          color: '#ffffff',
          borderRadius: '4px',
          maxWidth: 'none',
          boxShadow: 'none',
          padding: '16px',
          border: '1px solid #3A3A3A',
          opacity: isTourSyncing ? 0 : 1,
          visibility: isTourSyncing ? 'hidden' : 'visible',
          pointerEvents: isTourSyncing ? 'none' : 'auto',
        }),

        close: (base) => ({
          ...base,
          color: '#ffffff',
          top: '10px',
          right: '10px',
          width: '28px',
          height: '28px',
          padding: '6px',
          borderRadius: '6px',
          cursor: 'pointer',
          zIndex: 5,
          opacity: isTourSyncing ? 0 : 1,
          pointerEvents: isTourSyncing ? 'none' : 'auto',
        }),

        badge: (base) => ({
          ...base,
          color: '#ffffff',
          backgroundColor: '#473F50',
          top: '8px',
          left: '8px',
          borderRadius: '8px',
          opacity: isTourSyncing ? 0 : 1,
          pointerEvents: isTourSyncing ? 'none' : 'auto',
        }),

        controls: (base) => ({
          ...base,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          marginTop: '0px !important',
          opacity: isTourSyncing ? 0 : 1,
          pointerEvents: isTourSyncing ? 'none' : 'auto',
        }),
      }}
      afterOpen={disableBody}
      beforeClose={enableBody}
      scrollSmooth
    >
      {children}
    </TourProvider>
  );
};

export default TourWrapper;
